/*========================================================================
 * $Id$
 *
 * Copyright © Mindbreeze Software GmbH, Linz, Austria, 2005-2013
 *
 * Der Nutzer des Computerprogramms anerkennt, dass der oben stehende
 * Copyright-Vermerk im Sinn des Welturheberrechtsabkommens an der vom
 * Urheber festgelegten Stelle in der Funktion des Computerprogramms
 * angebracht bleibt, um den Vorbehalt des Urheberrechtes genuegend zum
 * Ausdruck zu bringen. Dieser Urheberrechtsvermerk darf weder vom Kunden,
 * Nutzer und/oder von Dritten entfernt, veraendert oder disloziert werden.
 * =========================================================================*/


define([
    "utils/trace",
    "underscore",
    "backbone",
    "model/readonlytree",
    "utils/clone",
    "model/model_response_observer"
  ], function(
    Trace,
    _,
    Backbone,
    ReadOnlyTreeModel,
    deepClone,
    ModelResponseObserver
  ) {

  /*
    output = compute(input, "change")
   */
  var ComputeWiring = function(input, output, options) {
      options = options || {};
      this.input = input;
      this.output = output;
      this.computedInput = new ReadOnlyTreeModel({});
      this.computeOn = options.computeOn || "change";
      this.listenToChannel = options.listenToChannel;

      this.defaultResponseOptions = options.defaultResponseOptions || {};
      this.defaultObserverOptions = options.defaultObserverOptions || {};
      this.ResponseObserverConstructor = options.responseObserverConstructor || ModelResponseObserver;

      this.providers = [];

      this.initialize.apply(this, arguments);
      this.activate(options);
  }

  _.extend(ComputeWiring.prototype,
           Backbone.Events,
  {
    initialize: function() {},

    isActivated: function () {
      return this._activated;
    },

    deactivate: function(options) {
      this._activated = false;
      this.input.setSilent(true);
      if (this.getListenToChannel()) this.stopListening(this.channel, this.computeOn, this.compute);
      this.stopListening(this.input, this.computeOn, this.compute);
    },

    activate: function(options) {
      this._activated = true;
      // don't listen twice
      if (this.getListenToChannel()) this.stopListening(this.channel, this.computeOn, this.compute);
      this.stopListening(this.input, this.computeOn, this.compute);
      if (this.getListenToChannel()) this.listenTo(this.channel, this.computeOn, this.compute);
      this.listenTo(this.input, this.computeOn, this.compute);
      this.input.setSilent(false);
      if (!options || !options.silent) {
        this.input.change();
      }
    },

    compute: function(ignored, options) {

      options = options || {};

      var that = this;

      var traceContext = options.traceContext;
      if (traceContext) traceContext = traceContext.willLeave();
      var leaveServiceCall = Trace.events.serviceCall.enter(traceContext && traceContext.context, method);
      var leaveSearchCall = Trace.events.search.enter(leaveServiceCall.context, count, query, nextPage, previousPage);

      // we use a private observer per call to know that the only events we receive,
      // are for this particular computation.    
      var extendedResponseOptions = _.extend(
        {},
        this.defaultResponseOptions,
        { traceContext: leaveSearchCall }
      );
      var responseObserver = new this.ResponseObserverConstructor(
        this.output,
        extendedResponseOptions,
        this.defaultObserverOptions
      );

      _.extend(responseObserver, {
        modelOutputPromises: []
      });

      var modelComputedInputPromise;

      try {
        modelComputedInputPromise = this.computedInput.load(deepClone(this.input.get()));
      } catch (ignored) {
        // NOOP
      }

      if (this.input && this.input.validate && !this.input.validate()) {
        if (this._responseObserver) {          
          this.stopListening(this._responseObserver, "response", this._handleComputationResponse)
          this._responseObserver = null;
        }

        var resetPromise = this.output.reset(null, {invalid:true});

        if (resetPromise) {
          return resetPromise.then(function() {
            that.trigger("computed" /*,{ traceContext: leaveSearchCall }*/);
          })
        }
      }

      // TODO: move to search wiring
      var method = "search",
          count,
          query,
          nextPage,
          previousPage;

      if (this.input && this.input.get) {
        count = this.input.get("count");
        query = this.input.get("user.query.unparsed");
        nextPage = this.input.get("paging.direction") === "NEXT" ? "TRUE" : "FALSE";
        previousPage = this.input.get("paging.direction") === "PREVIOUS" ? "TRUE" : "FALSE";
      }

      // there is still a computation being performed
      //   - stop listening outcomes of the old computation
      //   - set it to aborted state
      if (this._responseObserver != null) {
        this.stopListening(this._responseObserver);
        this.trigger("aborted");
        this._responseObserver = null;
      }

      this.trigger("computing");
      this._responsesReceived = 0; // keep track of how many response we received
      this._responseObserver = responseObserver;

      this.listenTo(this._responseObserver, "response", function () {
        if (this._responseObserver == responseObserver) {
          this._handleComputationResponse.apply(this, arguments);
        }
        // else this._responseObserver already has changed (another computation)
      });

      // the second argument will either receive multiple responses or error messages
      var responsePromise = this.performComputation(this.input, responseObserver);

      responsePromise.always(function(){
        // TODO: Error condition, if we did _not_ have at least one
        //       successful response?

        // if this is not the case another computation overtook us, and we can ignore our result
        if (that._responseObserver == responseObserver) {
          that._responseObserver = null;

          responseObserver.onModelActionsFinished(
            function() {
              if(responseObserver.finalize) responseObserver.finalize();

              that.triggerComputed({ traceContext: leaveSearchCall });

              var resultsCount = that.output.get("resultset.results_count");
              var estimatedCount = that.output.get("estimated_count") || 0;
  
              leaveSearchCall.leave(resultsCount || 0, estimatedCount, estimatedCount > 0 ? "FALSE" : "TRUE");
              leaveServiceCall.leave();
              if (traceContext) traceContext.leave();
            }, 
            modelComputedInputPromise
          );
        }
      });
    },

    _handleComputationResponse: function(response, channel, observer, computing) {
      var received = this._responsesReceived++;
      var reset = received == 0;
      var additionalModelOptions = this._responseObserver && this._responseObserver.getAdditionalModelOptions 
      ? this._responseObserver.getAdditionalModelOptions() 
      : {};

      var options = _.extend(
        { 
          additionalAttributes: { 
            computing: computing 
          },
          parse: true,
          channel: channel,
        },
        additionalModelOptions
      );

      var modelAction = reset 
        ? this._responseObserver.resetModel
        : this._responseObserver.updateModel;
        if (modelAction) {
          modelAction.apply(this._responseObserver, [response, options])
          .catch(function(error) {
              if (console && console.error) console.error(error);
          });
        }
    },

    getListenToChannel: function () {
      return this.listenToChannel !== false && this.channel && this.channel.on;
    },

    triggerComputed: function (options) {
      this.trigger("computed", _.extend(options, {supportsAllComputationFinished: true }));
      const additionalTasks = this.providers.map(provider => provider.getComputationTask());
      Promise.allSettled(additionalTasks).then((_resolvedOrRejectedAdditionalTasks) => {
        this.trigger("allcomputationfinished");
      });
    },

    registerComputationTaskProvider: function(provider) {
      this.providers.push(provider);
    },

    unregisterComputationTaskProvider: function(provider) {
      this.providers = this.providers.filter(providerObj => providerObj != provider);
    }

  });

  ComputeWiring.extend = Backbone.Model.extend;
  return ComputeWiring;
});
